import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Popover } from '@lifeomic/chroma-react/components/Popover';
import { Terminal, Clipboard } from '@lifeomic/chromicons';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useNotify } from '../../hooks/useNotify';
import {
  IconButton,
  IconButtonProps,
} from '@lifeomic/chroma-react/components/IconButton';
import * as React from 'react';
import copy from 'copy-to-clipboard';

const messages = defineMessages({
  title: {
    id: 'phc-web-toolkit.CLIHelp.title',
    defaultMessage: 'Run in the LifeOmic CLI',
  },
  installLink: {
    id: 'phc-web-toolkit.CLIHelp.installLink',
    defaultMessage: 'How to install LifeOmic CLI',
  },
  copiedCommand: {
    id: 'phc-web-toolkit.CLIHelp.copiedCommand',
    defaultMessage: 'Copied the command to the clipboard',
  },
  tooltip: {
    id: 'phc-web-toolkit.CLIHelp.tooltip',
    defaultMessage: 'View CLI Help',
  },
  modalTitle: {
    id: 'phc-web-toolkit.CLIHelp.modalTitle',
    defaultMessage: 'LifeOmic CLI',
  },
  close: {
    id: 'phc-web-toolkit.CLIHelp.close',
    defaultMessage: 'Close',
  },
  copyCommand: {
    id: 'phc-web-toolkit.CLIHelp.copyCommand',
    defaultMessage: 'Copy command to clipboard',
  },
  lifeomicCLIDetails: {
    id: 'phc-web-toolkit.CLIHelp.lifeomicCLIDetails',
    defaultMessage: 'LifeOmic CLI details',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    color: theme.palette.common.black,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  popover: {
    maxWidth: '30rem',
    [theme.breakpoints.down('md')]: {
      maxWidth: '20rem',
    },
  },
  codeButtonBox: {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  code: {
    fontFamily: theme.typography.fontFamilyMonospace,
    overflowX: 'scroll',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      whiteSpace: 'pre-wrap',
    },
  },
  link: {
    fontSize: theme.typography.subtitle1.fontSize,
  },
}));

interface CLIHelpProps {
  color?: IconButtonProps['color'];
  children: string;
  variant?: 'popover' | 'modal';
}

export const CLIHelp: React.FC<CLIHelpProps> = ({
  color,
  children,
  variant = 'popover',
  ...rootProps
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [addNotification] = useNotify();

  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

  const button = (
    <Tooltip title={formatMessage(messages.tooltip)}>
      <IconButton
        aria-label={formatMessage(messages.tooltip)}
        icon={Terminal}
        size={0}
        color={color}
        onClick={
          variant === 'popover'
            ? undefined
            : e => {
                e.stopPropagation();
                e.currentTarget.blur();
                setIsDialogOpen(true);
              }
        }
        {...rootProps}
      />
    </Tooltip>
  );

  const content = (
    <FormBox className={classes.content}>
      <Text size="subbody">{formatMessage(messages.title)}</Text>
      <Box align="center" className={classes.codeButtonBox}>
        <code className={classes.code}>{children}</code>
        <IconButton
          aria-label={formatMessage(messages.copyCommand)}
          icon={Clipboard}
          size={0}
          onClick={() => {
            copy(children);
            addNotification(formatMessage(messages.copiedCommand), 'success');
          }}
        />
      </Box>
      <Link
        className={classes.link}
        to="https://github.com/lifeomic/cli"
        newTab
      >
        {formatMessage(messages.installLink)}
      </Link>
    </FormBox>
  );

  if (variant === 'popover') {
    return (
      <Popover
        aria-label={formatMessage(messages.lifeomicCLIDetails)}
        placement="bottom-start"
        anchorElement={<span>{button}</span>}
        className={classes.popover}
      >
        {content}
      </Popover>
    );
  }

  return (
    <>
      {button}
      {isDialogOpen && (
        <Modal
          title={formatMessage(messages.modalTitle)}
          onDismiss={() => setIsDialogOpen(false)}
          isOpen={isDialogOpen}
          size={1}
          actions={
            <Button variant="text" onClick={() => setIsDialogOpen(false)}>
              {formatMessage(messages.close)}
            </Button>
          }
        >
          {content}
        </Modal>
      )}
    </>
  );
};
